import { Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { StorageAdapterResolver } from '../media/storage-adapter-resolver.service';

export interface LoadedObject {
  body: Buffer;
  /** Stable identity for the derivative cache key. */
  cacheKey: string;
}

/**
 * Lets the derivative endpoint read source images out of object storage.
 *
 * Abstract class rather than an interface so it can be a Nest injection token
 * while `ImageDerivativeService` still constructs cleanly without it in specs.
 */
export abstract class MediaObjectSource {
  abstract load(src: string): Promise<LoadedObject | null>;
}

/**
 * Resolves `?src=` against the media registry.
 *
 * The lookup is by storage key, which is also the security boundary here: the
 * endpoint is public (product photos are public), so an arbitrary path must
 * not become an arbitrary read. Only keys that exist as a registered,
 * non-private asset resolve — a caller cannot walk another organization's
 * prefix, because an unregistered key simply returns null and falls through to
 * the disk path, which enforces its own containment check.
 */
@Injectable()
export class MediaObjectSourceService extends MediaObjectSource {
  constructor(
    private readonly prisma: PrismaService,
    private readonly resolver: StorageAdapterResolver
  ) {
    super();
  }

  async load(src: string): Promise<LoadedObject | null> {
    const key = src.replace(/^\/+/, '');

    const asset = await this.prisma.mediaAsset.findFirst({
      where: {
        storageKey: key,
        status: { in: ['active', 'trashed'] },
        // Documents are private and served through signed URLs; they must not
        // be reachable through an unauthenticated resize endpoint.
        ownerType: { not: 'document' },
      },
      select: { id: true, storageConfigId: true, storageKey: true, checksum: true, updatedAt: true },
    });
    if (!asset) return null;

    const config = await this.resolver.configById(asset.storageConfigId);
    // Local-disk assets are left to the fs path, which already handles them
    // and avoids a pointless adapter round trip.
    if (config.mode === 'local_disk') return null;

    const adapter = await this.resolver.forConfig(config, 'upload');
    const body = await adapter.read(asset.storageKey);

    return {
      body,
      // Keys are immutable — a replacement writes a new key — so the key plus
      // the row's updatedAt is enough to never serve a stale derivative.
      cacheKey: `${asset.storageConfigId}|${asset.storageKey}|${asset.updatedAt.getTime()}`,
    };
  }
}
